import { useEffect, useState } from "react";
import { useAuth } from "@/modules/auth";
import { TagList } from "@/modules/tag";
import { useTags } from "@/modules/tag/hooks/use-tags";
import { CreateTagModal } from "@/modules/tag/components/create-tag-modal";
import { EditTagModal } from "@/modules/tag/components/edit-tag-modal";
import Button from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import type { Tag } from "@/modules/tag/types";
import TagListSkeleton from "@/modules/tag/components/tag-list-skeleton";
import Container from "@/components/ui/container";
import { useOnboardingStore } from "@/modules/onboarding/stores/onboarding-store";

export default function TagsPage() {
	const { currentWorkspaceId } = useAuth();
	const { tags, indexTags, deleteTag } = useTags();
	const { showToast } = useToast();
	const [loading, setLoading] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
	const [nextCursor, setNextCursor] = useState<string | null>(null);

	useEffect(() => {
		if (!currentWorkspaceId) return;

		// Fetch tags for the current workspace
		(async () => {
			setLoading(true);
			const response = await indexTags(currentWorkspaceId);
			if (response) {
				setNextCursor(response.meta.next_cursor);
				if (response.data && response.data.length > 0) {
					const store = useOnboardingStore.getState();
					if (!store.completedSteps.includes("setup_tags")) {
						store.completeStep(currentWorkspaceId, "setup_tags");
					}
				}
			}
			setLoading(false);
		})();
	}, [currentWorkspaceId, indexTags]);

	const handleLoadMore = async () => {
		if (!currentWorkspaceId || !nextCursor || isLoadingMore) return;

		setIsLoadingMore(true);
		const response = await indexTags(currentWorkspaceId, nextCursor);
		if (response) {
			setNextCursor(response.meta.next_cursor);
		}
		setIsLoadingMore(false);
	};

	const handleDelete = async (id: string, workspaceId: string) => {
		await deleteTag(id, workspaceId);
		showToast({
			title: "Tag deleted",
			description: "The tag has been deleted successfully.",
			status: "success",
		});
	};

	const handleEdit = (tag: Tag) => {
		setSelectedTag(tag);
		setIsEditModalOpen(true);
	};

	const handleEditSuccess = async () => {
		// Refresh tags after editing
		if (currentWorkspaceId) {
			await indexTags(currentWorkspaceId);
		}
		showToast({
			title: "Tag updated",
			description: "The tag has been updated successfully.",
			status: "success",
		});
	};

	const handleCreateSuccess = async () => {
		// Refresh tags after creating a new one
		if (currentWorkspaceId) {
			await indexTags(currentWorkspaceId);
		}
		showToast({
			title: "Tag created",
			description: "The tag has been created successfully.",
			status: "success",
		});
	};

	const hasNoTags = !loading && tags !== null && tags.length === 0;

	if (hasNoTags) {
		return (
			<div className="py-12">
				<div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
					<h2 className="text-xl font-semibold text-primary mb-2">No tags yet</h2>
					<p className="text-tertiary text-center max-w-md mb-8">Tags help you organize testimonials by topic, product, or campaign.</p>

					<button
						type="button"
						onClick={() => setIsCreateModalOpen(true)}
						className="flex items-center gap-3.5 px-6 py-5 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors text-left cursor-pointer"
					>
						<svg className="w-5 h-5 text-muted shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
						</svg>
						<div className="min-w-0">
							<h3 className="font-medium text-primary text-t-15">Create your first tag</h3>
							<p className="text-t-13 text-tertiary">Add a tag to start organizing testimonials</p>
						</div>
					</button>
				</div>

				<CreateTagModal open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen} onSuccess={handleCreateSuccess} workspaceId={currentWorkspaceId || ""} />
			</div>
		);
	}

	return (
		<div className="py-12 space-y-4">
			<Container>
				<div className="flex items-center justify-between mb-6">
					<div>
						<h1 className="text-xl font-semibold text-primary">Tags</h1>
						<p className="mt-1 text-tertiary max-w-[640px]">Manage and maintain your tags to keep testimonials well organized. You can update tag names or remove tags at any time.</p>
					</div>
					<div>
						<Button onClick={() => setIsCreateModalOpen(true)} className="whitespace-nowrap">
							Create Tag
						</Button>
					</div>
				</div>

				<div>{loading ? <TagListSkeleton /> : tags && tags.length > 0 ? <TagList tags={tags} workspaceId={currentWorkspaceId || ""} onDelete={handleDelete} onEdit={handleEdit} hasMore={!!nextCursor} isLoadingMore={isLoadingMore} onLoadMore={handleLoadMore} /> : null}</div>
			</Container>

			<CreateTagModal open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen} onSuccess={handleCreateSuccess} workspaceId={currentWorkspaceId || ""} />
			<EditTagModal open={isEditModalOpen} onOpenChange={setIsEditModalOpen} onSuccess={handleEditSuccess} workspaceId={currentWorkspaceId || ""} tag={selectedTag} />
		</div>
	);
}
